import { useState } from 'react';

export default function SupportProcess() {
  const [formData, setFormData] = useState({
    supportType: '',
    amount: '',
    duration: '',
    name: '',
    birthDate: '',
    phone: '',
    email: '',
    address: '',
    church: '',
    position: '',
    prayerSupport: false,
    agreement: false
  });

  const [isSubmitting, setIsSubmitting] = useState(false);
  const [submitStatus, setSubmitStatus] = useState<'idle' | 'success' | 'error'>('idle');
  const [copied, setCopied] = useState(false);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement | HTMLSelectElement>) => {
    const { name, value, type } = e.target;
    if (type === 'checkbox') {
      const checked = (e.target as HTMLInputElement).checked;
      setFormData(prev => ({ ...prev, [name]: checked }));
    } else {
      setFormData(prev => ({ ...prev, [name]: value }));
    }
  };

  const handleCopyAccount = () => {
    navigator.clipboard.writeText('301-0368-9129-01');
    setCopied(true);
    setTimeout(() => setCopied(false), 2000);
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!formData.supportType || !formData.amount || !formData.name || !formData.birthDate || !formData.phone || !formData.agreement) {
      return;
    }

    setIsSubmitting(true);
    setSubmitStatus('idle');

    try {
      const formBody = new URLSearchParams();
      formBody.append('supportType', formData.supportType);
      formBody.append('amount', formData.amount + '원');
      formBody.append('duration', formData.duration || '미선택');
      formBody.append('name', formData.name);
      formBody.append('birthDate', formData.birthDate);
      formBody.append('phone', formData.phone);
      formBody.append('email', formData.email || '미입력');
      formBody.append('address', formData.address || '미입력');
      formBody.append('church', formData.church || '미입력');
      formBody.append('position', formData.position || '미입력');
      formBody.append('prayerSupport', formData.prayerSupport ? '동참' : '미동참');

      const response = await fetch('https://readdy.ai/api/form/d65ud9594rvd6t186hq0', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/x-www-form-urlencoded',
        },
        body: formBody.toString()
      });

      if (response.ok) {
        setSubmitStatus('success');
        setFormData({
          supportType: '',
          amount: '',
          duration: '',
          name: '',
          birthDate: '',
          phone: '',
          email: '',
          address: '',
          church: '',
          position: '',
          prayerSupport: false,
          agreement: false
        });
      } else {
        setSubmitStatus('error');
      }
    } catch (error) {
      setSubmitStatus('error');
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <section id="support-process" className="py-16 sm:py-20 md:py-24 bg-gradient-to-b from-gray-50 to-white">
      <div className="max-w-7xl mx-auto px-4 sm:px-6">
        {/* Section Header */}
        <div className="text-center mb-12 sm:mb-16">
          <span className="inline-block px-4 py-1.5 bg-teal-100 text-teal-700 text-xs sm:text-sm font-semibold rounded-full mb-4">
            STEP 3 · ACTION
          </span>
          <h2 className="text-2xl sm:text-3xl md:text-4xl lg:text-5xl font-bold text-gray-900 mb-4">
            입금 계좌 및 온라인 신청
          </h2>
        </div>

        {/* Bank Account Card */}
        <div className="max-w-3xl mx-auto mb-12 sm:mb-16">
          <div className="bg-gradient-to-br from-teal-600 via-teal-700 to-teal-800 rounded-2xl sm:rounded-3xl p-6 sm:p-8 md:p-10 shadow-2xl relative overflow-hidden">
            {/* Decorative Elements */}
            <div className="absolute top-0 right-0 w-40 h-40 bg-white/5 rounded-full -translate-y-1/2 translate-x-1/2"></div>
            <div className="absolute bottom-0 left-0 w-32 h-32 bg-white/5 rounded-full translate-y-1/2 -translate-x-1/2"></div>
            
            <div className="relative z-10 text-center">
              <div className="inline-flex items-center justify-center w-14 h-14 sm:w-16 sm:h-16 bg-white/20 rounded-full mb-4 sm:mb-6">
                <i className="ri-bank-line text-2xl sm:text-3xl text-white"></i>
              </div>
              <h3 className="text-lg sm:text-xl md:text-2xl font-bold text-white/90 mb-2">공식 후원 계좌</h3>
              <p className="text-white/70 text-sm sm:text-base mb-4 sm:mb-6">NH농협은행</p>
              
              <div className="bg-white/10 backdrop-blur-sm rounded-xl sm:rounded-2xl p-4 sm:p-6 md:p-8 mb-4 sm:mb-6">
                <p className="text-3xl sm:text-4xl md:text-5xl lg:text-6xl font-bold text-white tracking-wider mb-2 sm:mb-3">
                  301-0368-9129-01
                </p>
                <p className="text-white/80 text-sm sm:text-base md:text-lg">
                  예금주: <span className="font-semibold text-white">킹덤브릿지미션</span>
                </p>
              </div>
              
              <button
                onClick={handleCopyAccount}
                className="inline-flex items-center px-5 sm:px-6 py-2.5 sm:py-3 bg-white text-teal-700 font-semibold rounded-full hover:bg-gray-100 transition-all cursor-pointer whitespace-nowrap text-sm sm:text-base"
              >
                <i className={`${copied ? 'ri-check-line' : 'ri-file-copy-line'} mr-2`}></i>
                {copied ? '복사 완료!' : '계좌번호 복사'}
              </button>
            </div>
          </div>
        </div>

        {/* Online Support Form */}
        <div className="max-w-4xl mx-auto">
          <div className="bg-white rounded-2xl sm:rounded-3xl shadow-xl border border-gray-100 overflow-hidden">
            {/* Form Header */}
            <div className="bg-gradient-to-r from-teal-50 to-emerald-50 px-6 sm:px-8 md:px-10 py-6 sm:py-8 border-b border-gray-100">
              <div className="flex items-center justify-center mb-3">
                <div className="w-10 h-10 sm:w-12 sm:h-12 bg-teal-600 rounded-full flex items-center justify-center mr-3">
                  <i className="ri-file-list-3-line text-lg sm:text-xl text-white"></i>
                </div>
                <h3 className="text-xl sm:text-2xl md:text-3xl font-bold text-gray-900">
                  온라인 후원 약정서
                </h3>
              </div>
              <p className="text-center text-xs sm:text-sm text-gray-600">
                홈페이지에서 바로 입력하고 제출하기를 누르세요
              </p>
            </div>

            <form id="support-process-form" data-readdy-form onSubmit={handleSubmit} className="p-6 sm:p-8 md:p-10 space-y-8">
              {/* Section 1: Support Information */}
              <div className="space-y-5">
                <div className="flex items-center mb-4">
                  <span className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-teal-600 text-white rounded-full text-sm sm:text-base font-bold mr-3">1</span>
                  <h4 className="text-base sm:text-lg md:text-xl font-bold text-gray-900">
                    후원 정보 <span className="text-red-500 text-sm">(필수)</span>
                  </h4>
                </div>
                
                <div className="pl-11 sm:pl-13 space-y-5">
                  {/* Support Type */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      구분 <span className="text-red-500">*</span>
                    </label>
                    <div className="flex flex-wrap gap-4">
                      <label className="flex items-center px-5 py-3 border-2 rounded-xl cursor-pointer transition-all hover:border-teal-300 has-[:checked]:border-teal-600 has-[:checked]:bg-teal-50">
                        <input
                          type="radio"
                          name="supportType"
                          value="정기후원"
                          checked={formData.supportType === '정기후원'}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-teal-600 cursor-pointer"
                          required
                        />
                        <span className="ml-2 text-sm sm:text-base font-medium text-gray-700">정기후원</span>
                      </label>
                      <label className="flex items-center px-5 py-3 border-2 rounded-xl cursor-pointer transition-all hover:border-teal-300 has-[:checked]:border-teal-600 has-[:checked]:bg-teal-50">
                        <input
                          type="radio"
                          name="supportType"
                          value="일시후원"
                          checked={formData.supportType === '일시후원'}
                          onChange={handleInputChange}
                          className="w-4 h-4 text-teal-600 cursor-pointer"
                          required
                        />
                        <span className="ml-2 text-sm sm:text-base font-medium text-gray-700">일시후원</span>
                      </label>
                    </div>
                  </div>

                  {/* Amount */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      후원 금액 <span className="text-red-500">*</span>
                    </label>
                    <div className="relative">
                      <input
                        type="text"
                        name="amount"
                        value={formData.amount}
                        onChange={handleInputChange}
                        placeholder="금액을 입력하세요"
                        className="w-full px-4 py-3 sm:py-3.5 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm sm:text-base pr-12"
                        required
                      />
                      <span className="absolute right-4 top-1/2 -translate-y-1/2 text-gray-500 font-medium">원</span>
                    </div>
                  </div>

                  {/* Duration */}
                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-3">
                      후원 기간
                    </label>
                    <div className="flex flex-wrap gap-2 sm:gap-3">
                      {['1년', '2년', '3년', '계속'].map((duration) => (
                        <label 
                          key={duration} 
                          className="flex items-center px-4 sm:px-5 py-2.5 border-2 rounded-full cursor-pointer transition-all hover:border-teal-300 has-[:checked]:border-teal-600 has-[:checked]:bg-teal-50"
                        >
                          <input
                            type="radio"
                            name="duration"
                            value={duration}
                            checked={formData.duration === duration}
                            onChange={handleInputChange}
                            className="w-3.5 h-3.5 text-teal-600 cursor-pointer"
                          />
                          <span className="ml-2 text-sm font-medium text-gray-700">{duration}</span>
                        </label>
                      ))}
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 2: Supporter Information */}
              <div className="space-y-5">
                <div className="flex items-center mb-4">
                  <span className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-teal-600 text-white rounded-full text-sm sm:text-base font-bold mr-3">2</span>
                  <h4 className="text-base sm:text-lg md:text-xl font-bold text-gray-900">
                    후원자 정보 <span className="text-red-500 text-sm">(필수)</span>
                  </h4>
                </div>
                
                <div className="pl-11 sm:pl-13 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        성명 <span className="text-red-500">*</span>
                      </label>
                      <input
                        type="text"
                        name="name"
                        value={formData.name}
                        onChange={handleInputChange}
                        placeholder="이름을 입력하세요"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm sm:text-base"
                        required
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        생년월일 <span className="text-red-500">*</span>
                        <span className="text-xs text-gray-500 ml-1">(기부금 영수증 발급용)</span>
                      </label>
                      <input
                        type="text"
                        name="birthDate"
                        value={formData.birthDate}
                        onChange={handleInputChange}
                        placeholder="예: 19900101"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm sm:text-base"
                        required
                      />
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-gray-700 mb-2">
                      핸드폰 번호 <span className="text-red-500">*</span>
                      <span className="text-xs text-gray-500 ml-1">(뉴스레터 수신용)</span>
                    </label>
                    <input
                      type="tel"
                      name="phone"
                      value={formData.phone}
                      onChange={handleInputChange}
                      placeholder="010-0000-0000"
                      className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm sm:text-base"
                      required
                    />
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        이메일
                      </label>
                      <input
                        type="email"
                        name="email"
                        value={formData.email}
                        onChange={handleInputChange}
                        placeholder="example@email.com"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm sm:text-base"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        주소
                      </label>
                      <input
                        type="text"
                        name="address"
                        value={formData.address}
                        onChange={handleInputChange}
                        placeholder="주소를 입력하세요"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm sm:text-base"
                      />
                    </div>
                  </div>
                </div>
              </div>

              {/* Section 3: Additional Information */}
              <div className="space-y-5">
                <div className="flex items-center mb-4">
                  <span className="flex items-center justify-center w-8 h-8 sm:w-10 sm:h-10 bg-gray-400 text-white rounded-full text-sm sm:text-base font-bold mr-3">3</span>
                  <h4 className="text-base sm:text-lg md:text-xl font-bold text-gray-900">
                    추가 정보 <span className="text-gray-500 text-sm font-normal">(선택)</span>
                  </h4>
                </div>
                
                <div className="pl-11 sm:pl-13 space-y-4">
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        섬기는 교회
                      </label>
                      <input
                        type="text"
                        name="church"
                        value={formData.church}
                        onChange={handleInputChange}
                        placeholder="교회명을 입력하세요"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm sm:text-base"
                      />
                    </div>
                    <div>
                      <label className="block text-sm font-medium text-gray-700 mb-2">
                        직분
                      </label>
                      <input
                        type="text"
                        name="position"
                        value={formData.position}
                        onChange={handleInputChange}
                        placeholder="예: 집사, 장로 등"
                        className="w-full px-4 py-3 border-2 border-gray-200 rounded-xl focus:ring-2 focus:ring-teal-500 focus:border-teal-500 text-sm sm:text-base"
                      />
                    </div>
                  </div>

                  <label className="flex items-center p-4 bg-gray-50 rounded-xl cursor-pointer hover:bg-gray-100 transition-colors">
                    <input
                      type="checkbox"
                      name="prayerSupport"
                      checked={formData.prayerSupport}
                      onChange={handleInputChange}
                      className="w-5 h-5 text-teal-600 rounded cursor-pointer"
                    />
                    <span className="ml-3 text-sm sm:text-base text-gray-700">기도 후원에도 동참하겠습니다</span>
                  </label>
                </div>
              </div>

              {/* Agreement */}
              <div className="bg-gradient-to-r from-teal-50 to-emerald-50 rounded-xl p-5 sm:p-6 border border-teal-100">
                <label className="flex items-start cursor-pointer">
                  <input
                    type="checkbox"
                    name="agreement"
                    checked={formData.agreement}
                    onChange={handleInputChange}
                    className="w-5 h-5 sm:w-6 sm:h-6 text-teal-600 rounded cursor-pointer mt-0.5 flex-shrink-0"
                    required
                  />
                  <span className="ml-3 text-sm sm:text-base text-gray-700 leading-relaxed">
                    "이와 같이 킹덤브릿지미션을 위하여 기도하며 후원하며 하나님 나라 확장과 선교사역에 동참하겠습니다." 
                    <span className="text-red-500 ml-1">*</span>
                  </span>
                </label>
              </div>

              {/* Submit Button */}
              <div className="text-center pt-4">
                <button
                  type="submit"
                  disabled={isSubmitting}
                  className="w-full sm:w-auto px-10 sm:px-16 py-4 sm:py-5 bg-gradient-to-r from-teal-600 to-teal-700 text-white text-base sm:text-lg font-bold rounded-full hover:from-teal-700 hover:to-teal-800 transition-all shadow-lg hover:shadow-xl disabled:from-gray-400 disabled:to-gray-500 disabled:cursor-not-allowed whitespace-nowrap cursor-pointer"
                >
                  {isSubmitting ? (
                    <span className="flex items-center justify-center">
                      <i className="ri-loader-4-line animate-spin mr-2"></i>
                      제출 중...
                    </span>
                  ) : (
                    <span className="flex items-center justify-center">
                      <i className="ri-send-plane-fill mr-2"></i>
                      제출하기
                    </span>
                  )}
                </button>
              </div>

              {/* Status Messages */}
              {submitStatus === 'success' && (
                <div className="bg-green-50 border-2 border-green-200 rounded-xl p-5 text-center">
                  <div className="w-12 h-12 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-check-line text-2xl text-green-600"></i>
                  </div>
                  <p className="text-base sm:text-lg text-green-800 font-bold mb-1">후원 약정서가 성공적으로 제출되었습니다!</p>
                  <p className="text-sm text-green-700">곧 연락드리겠습니다. 감사합니다. 🙏</p>
                </div>
              )}

              {submitStatus === 'error' && (
                <div className="bg-red-50 border-2 border-red-200 rounded-xl p-5 text-center">
                  <div className="w-12 h-12 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-3">
                    <i className="ri-error-warning-line text-2xl text-red-600"></i>
                  </div>
                  <p className="text-base sm:text-lg text-red-800 font-bold mb-1">제출 중 오류가 발생했습니다.</p>
                  <p className="text-sm text-red-700">다시 시도해주세요.</p>
                </div>
              )}
            </form>
          </div>
        </div>

        {/* Step 4: Transparency Section */}
        <div className="mt-16 sm:mt-20">
          <div className="text-center mb-10 sm:mb-12">
            <span className="inline-block px-4 py-1.5 bg-amber-100 text-amber-700 text-xs sm:text-sm font-semibold rounded-full mb-4">
              STEP 4 · TRANSPARENCY
            </span>
            <h3 className="text-xl sm:text-2xl md:text-3xl lg:text-4xl font-bold text-gray-900">
              투명성 및 행정 안내
            </h3>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-6 sm:gap-8 max-w-5xl mx-auto">
            {/* Tax Receipt */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <i className="ri-file-text-line text-3xl sm:text-4xl text-teal-600"></i>
              </div>
              <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">기부금 영수증 발급</h4>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                보내주신 모든 후원금은 <strong className="text-teal-600">연말정산 시 세금공제 혜택</strong>을 받으실 수 있습니다.
              </p>
            </div>

            {/* Financial Report */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <i className="ri-bar-chart-grouped-line text-3xl sm:text-4xl text-teal-600"></i>
              </div>
              <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">재정 보고</h4>
              <p className="text-sm sm:text-base text-gray-600 leading-relaxed">
                매년 정기총회를 통해 재정 사용 내역을 <strong className="text-teal-600">투명하게 공개</strong>하며, 홈페이지와 소식지를 통해 보고합니다.
              </p>
            </div>

            {/* Contact */}
            <div className="bg-white rounded-2xl p-6 sm:p-8 shadow-lg border border-gray-100 hover:shadow-xl transition-shadow text-center">
              <div className="w-16 h-16 sm:w-20 sm:h-20 bg-gradient-to-br from-teal-100 to-emerald-100 rounded-2xl flex items-center justify-center mx-auto mb-5">
                <i className="ri-customer-service-2-line text-3xl sm:text-4xl text-teal-600"></i>
              </div>
              <h4 className="text-lg sm:text-xl font-bold text-gray-900 mb-3">후원 문의</h4>
              <p className="text-sm sm:text-base text-gray-600 mb-4">
                궁금하신 사항이 있으시면<br />언제든지 연락주세요.
              </p>
              <a 
                href="tel:010-8524-9200" 
                className="inline-flex items-center px-5 py-2.5 bg-teal-600 text-white font-bold rounded-full hover:bg-teal-700 transition-colors cursor-pointer text-sm sm:text-base"
              >
                <i className="ri-phone-line mr-2"></i>
                010-8524-9200
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}
